// ============================================================================
// MarketState — единые состояния экранов витрины: загрузка, ошибка, пусто.
// Явные состояния вместо пустых списков и «вечных» спиннеров.
// ============================================================================

import type { ReactNode } from 'react';
import { AlertTriangle, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { MarketFailure } from '@/lib/market';

export function MarketLoading({ text = 'Загрузка…' }: { text?: string }) {
  return (
    <div className="flex items-center justify-center gap-2 py-12 text-sm text-muted-foreground">
      <Loader2 className="h-4 w-4 animate-spin" />
      {text}
    </div>
  );
}

export function MarketError({ failure, onRetry }: { failure: MarketFailure; onRetry?: () => void }) {
  return (
    <div className="rounded-2xl border border-destructive/30 bg-destructive/5 p-4">
      <div className="flex items-start gap-2 text-sm text-destructive">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
        <div className="min-w-0 flex-1 break-words">{failure.message}</div>
      </div>
      {onRetry && (
        <Button
          size="sm"
          variant="outline"
          className="mt-3 h-8 rounded-lg"
          onClick={onRetry}
        >
          <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
          Повторить
        </Button>
      )}
    </div>
  );
}

export function MarketEmpty({
  icon,
  title,
  text,
  action,
}: {
  icon?: ReactNode;
  title: string;
  text?: string;
  action?: ReactNode;
}) {
  return (
    <div className="rounded-2xl border border-dashed bg-card/50 px-5 py-10 text-center">
      {icon && <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-primary/10 text-primary">{icon}</div>}
      <div className="text-sm font-semibold">{title}</div>
      {text && <div className="mx-auto mt-1 max-w-sm text-xs leading-5 text-muted-foreground">{text}</div>}
      {action && <div className="mt-4 flex justify-center">{action}</div>}
    </div>
  );
}
